// src/components/work_orders/WorkOrderWatchdogPanel.js
// FlowSense MOW+ — Enterprise Work Order Watchdog Dashboard Panel
//
// Architecture:
//   - WebSocket subscriptions  : live updates from 'watchdog_events' + 'work_order_events'
//   - Global Context Provider  : consumes Watchdog health globally, zero redundant API polling
//   - Global Metrics Fetching  : GET /workorders/watchdog/metrics for 11k+ global stats
//   - Debounced re-renders     : WS bursts are coalesced before triggering state updates
//   - Circuit breaker UI       : mirrors backend CLOSED/HALF_OPEN/OPEN state visually
//   - Rolling metrics display  : shows 10-scan avg efficiency + avg scan duration
//   - Animated counters        : metrics animate in/out on change via Framer Motion
//   - Skeleton loading states  : first-load and scan-in-progress gracefully handled
//   - Per-stat quick actions   : each tile navigates/filters the table directly
//   - Expandable diagnostics   : collapsible backend health section for ops teams
//   - Memo + useCallback       : zero redundant renders; all handlers stable
//   - Graceful degradation     : panel works client-side even if health endpoint absent
//
// --- UPDATE (v2):
//   - FIX: the WebSocket effect depended on onSyncComplete; the page passed an inline arrow, so the
//     panel unsubscribed/resubscribed on every parent render (events could be missed in between).
//     Callbacks are read through refs — subscribe once.
//   - Also refreshes metrics (debounced) on work_order_created / _completed / _updated, plus every
//     60 s while the tab is visible, so the tiles never go stale between scans.
//   - "Stagnant" tile filters stagnant tickets (onApplyFilter('stagnant', true)) — it used to apply
//     status = in_progress, which also showed healthy in-progress jobs.
//   - (?) explainers on every tile and on Network Efficiency (what it means, why it matters).
//   - Dark mode: header title / icon backgrounds / AI box heading were light-only colours.

import React, {
    useState, useEffect, useRef, useCallback, memo,
} from 'react';
import {
    Box, Flex, Text, HStack, VStack, Icon, Badge, Button, Progress,
    useColorModeValue, useToast, Divider, SimpleGrid, Tooltip,
    Skeleton, Collapse, CircularProgress, CircularProgressLabel,
    Alert, AlertIcon, AlertDescription, useInterval,
    Stat, StatLabel, StatNumber, StatHelpText,
} from '@chakra-ui/react';
import {
    MdSecurity, MdAutorenew, MdOutlineAutoAwesome,
    MdErrorOutline, MdWarning, MdTimer, MdPersonOff, MdTrendingUp,
    MdCheckCircle, MdExpandMore, MdExpandLess, MdSpeed,
    MdNetworkCheck, MdBolt, MdHistoryToggleOff, MdFilterAlt,
    MdAnalytics, MdError,
} from 'react-icons/md';
import { FiActivity, FiAlertTriangle } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

import webSocketService from '../../services/websocketService';
import { getAuthToken } from '../../utils/auth';
import { config } from '../../config';
// --- UPDATE: socket messages are envelopes { type:'watchdog_events', payload:{ type:'scan_complete', … } }
import { liveEventBody } from '../../services/analyticsService';
// --- END UPDATE

// --- ENTERPRISE FIX: Import Operational Health from Global Context ---
import { useOperationalHealth } from '../../contexts/AIInsightsContext';
// --- UPDATE
import { WoHint } from './WorkOrderAiHelper';
const METRICS_POLL_MS = 60_000;
const LIVE_WO_EVENTS = ['work_order_created', 'work_order_completed', 'work_order_updated'];
// --- END UPDATE

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const MotionBox = motion(Box);

const API_BASE = config.apiUrl;

const DEBOUNCE_RENDER_MS      = 400;    // coalesce rapid WS bursts

const EFFICIENCY_THRESHOLDS = {
    HEALTHY:  90,
    WARNING:  70,
};

const CIRCUIT_CFG = {
    CLOSED:    { color: 'green',  label: 'Circuit Healthy',  icon: MdCheckCircle },
    HALF_OPEN: { color: 'yellow', label: 'Circuit Probing',  icon: MdWarning },
    OPEN:      { color: 'red',    label: 'Circuit OPEN',     icon: MdError },
};

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const efficiencyColor = (pct) => {
    if (pct >= EFFICIENCY_THRESHOLDS.HEALTHY) return 'green';
    if (pct >= EFFICIENCY_THRESHOLDS.WARNING)  return 'orange';
    return 'red';
};

const formatDuration = (ms) => {
    if (ms == null) return '—';
    if (ms < 1000)  return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
};

const relativeTime = (isoString) => {
    if (!isoString) return '—';
    const diff = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
    if (diff < 60)   return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return `${Math.floor(diff / 3600)}h ago`;
};

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENT: CIRCUIT BREAKER BADGE
// ─────────────────────────────────────────────────────────────────────────────
const CircuitBadge = memo(({ state }) => {
    const cfg = CIRCUIT_CFG[state] || CIRCUIT_CFG.CLOSED;
    return (
        <MotionBox
            animate={state === 'OPEN'
                ? { boxShadow: ['0 0 0px', `0 0 10px var(--chakra-colors-red-400)`, '0 0 0px'] }
                : {}
            }
            transition={{ duration: 1.8, repeat: Infinity }}
            borderRadius="full"
            display="inline-flex"
        >
            <Badge
                colorScheme={cfg.color}
                borderRadius="full"
                px={3} py={1}
                display="inline-flex"
                alignItems="center"
                gap={1}
                fontSize="xs"
                fontWeight="700"
            >
                <Icon as={cfg.icon} boxSize={3} />
                {cfg.label}
            </Badge>
        </MotionBox>
    );
});

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENT: ANIMATED METRIC COUNTER
// ─────────────────────────────────────────────────────────────────────────────
const AnimatedCount = memo(({ value, color }) => (
    <AnimatePresence mode="wait">
        <MotionBox
            key={value}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.25 }}
        >
            <Text
                fontSize="2xl"
                fontWeight="900"
                color={value > 0 ? `${color}.600` : 'gray.400'}
                lineHeight="1"
            >
                {value}
            </Text>
        </MotionBox>
    </AnimatePresence>
));

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENT: WATCHDOG STAT TILE
// ─────────────────────────────────────────────────────────────────────────────
const WatchdogStat = memo(({ label, count, icon, color, onClick, tooltip, isLoading, hint /* --- UPDATE */ }) => {
    const statBg     = useColorModeValue('gray.50', 'gray.750');
    const statBorder = useColorModeValue('gray.200', 'gray.600');

    if (isLoading) {
        return (
            <Box bg={statBg} p={3} borderRadius="lg" borderWidth="1px" borderColor={statBorder}>
                <Skeleton h="28px" w="40px" mb={2} />
                <Skeleton h="12px" w="70px" />
            </Box>
        );
    }

    return (
        <Tooltip label={tooltip} hasArrow placement="top">
            <Box
                bg={statBg}
                p={3}
                borderRadius="lg"
                borderWidth="1px"
                borderColor={count > 0 ? `${color}.300` : statBorder}
                cursor={count > 0 && onClick ? 'pointer' : 'default'}
                onClick={() => count > 0 && onClick?.()}
                _hover={count > 0 && onClick
                    ? {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 4px 14px var(--chakra-colors-${color}-100)`,
                        borderColor: `${color}.500`,
                    }
                    : {}
                }
                transition="all 0.2s"
                position="relative"
                overflow="hidden"
            >
                {/* Glow blob for non-zero tiles */}
                {count > 0 && (
                    <Box
                        position="absolute" top="-8px" right="-8px"
                        w="48px" h="48px" bg={`${color}.100`}
                        borderRadius="full" filter="blur(12px)" opacity={0.6}
                    />
                )}
                <HStack justify="space-between" mb={2}>
                    <Icon as={icon} color={count > 0 ? `${color}.500` : 'gray.400'} boxSize={5} />
                    <AnimatedCount value={count} color={color} />
                </HStack>
                <HStack spacing={0}>
                    <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" noOfLines={1}>
                        {label}
                    </Text>
                    {hint && <WoHint metric={hint} />}{/* --- UPDATE */}
                </HStack>
                {count > 0 && onClick && (
                    <HStack mt={1} spacing={1}>
                        <Icon as={MdFilterAlt} boxSize={2.5} color={`${color}.400`} />
                        <Text fontSize="2xs" color={`${color}.500`} fontWeight="600">Filter table</Text>
                    </HStack>
                )}
            </Box>
        </Tooltip>
    );
});

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENT: LAST SCAN INFO BAR
// ─────────────────────────────────────────────────────────────────────────────
const LastScanBar = memo(({ lastScan, isScanning }) => {
    const mutedColor  = useColorModeValue('gray.500', 'gray.400');
    const barBg       = useColorModeValue('gray.50', 'gray.750');
    const borderColor = useColorModeValue('gray.200', 'gray.700');

    if (!lastScan && !isScanning) return null;

    return (
        <Flex
            bg={barBg} px={4} py={2} borderRadius="lg"
            borderWidth="1px" borderColor={borderColor}
            align="center" justify="space-between" wrap="wrap" gap={2}
        >
            {isScanning ? (
                <HStack spacing={2}>
                    <MotionBox
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
                    >
                        <Icon as={MdAutorenew} color="blue.500" boxSize={4} />
                    </MotionBox>
                    <Text fontSize="xs" fontWeight="700" color="blue.500">
                        Backend scan in progress…
                    </Text>
                </HStack>
            ) : (
                <HStack spacing={4} flexWrap="wrap">
                    <HStack spacing={1}>
                        <Icon as={MdHistoryToggleOff} color={mutedColor} boxSize={3.5} />
                        <Text fontSize="xs" color={mutedColor} fontFamily="mono">
                            {lastScan.scan_id}
                        </Text>
                    </HStack>
                    <HStack spacing={1}>
                        <Icon as={MdTimer} color={mutedColor} boxSize={3.5} />
                        <Text fontSize="xs" color={mutedColor}>
                            {formatDuration(lastScan.duration_ms)}
                        </Text>
                    </HStack>
                    <HStack spacing={1}>
                        <Icon as={MdSpeed} color={mutedColor} boxSize={3.5} />
                        <Text fontSize="xs" color={mutedColor}>
                            {relativeTime(lastScan.timestamp)}
                        </Text>
                    </HStack>
                    {lastScan.corrections && (
                        <HStack spacing={1}>
                            <Icon as={MdBolt} color="orange.400" boxSize={3.5} />
                            <Text fontSize="xs" color="orange.500" fontWeight="600">
                                {Object.values(lastScan.corrections).reduce((s, v) => s + (v || 0), 0)} corrections applied
                            </Text>
                        </HStack>
                    )}
                </HStack>
            )}
        </Flex>
    );
});

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENT: BACKEND DIAGNOSTICS (Collapsible)
// ─────────────────────────────────────────────────────────────────────────────
const BackendDiagnostics = memo(({ healthReport, isOpen }) => {
    const mutedColor  = useColorModeValue('gray.500', 'gray.400');
    const diagBg      = useColorModeValue('gray.50', 'gray.750');
    const borderColor = useColorModeValue('gray.200', 'gray.700');

    if (!healthReport) return null;

    const rw = healthReport.rolling_window || {};

    return (
        <Collapse in={isOpen} animateOpacity>
            <Box mt={4} pt={4} borderTopWidth="1px" borderColor={borderColor}>
                <Text fontSize="xs" fontWeight="700" color={mutedColor}
                    textTransform="uppercase" letterSpacing="wider" mb={3}>
                    Backend Watchdog Diagnostics
                </Text>
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                    <Box bg={diagBg} p={3} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                        <Text fontSize="2xs" color={mutedColor} fontWeight="600" textTransform="uppercase">Total Scans</Text>
                        <Text fontSize="xl" fontWeight="800" fontFamily="mono">{healthReport.scan_count ?? '—'}</Text>
                    </Box>
                    <Box bg={diagBg} p={3} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                        <Text fontSize="2xs" color={mutedColor} fontWeight="600" textTransform="uppercase">Avg Efficiency (10)</Text>
                        <Text fontSize="xl" fontWeight="800" fontFamily="mono"
                            color={rw.avg_efficiency != null
                                ? `${efficiencyColor(rw.avg_efficiency)}.500`
                                : 'gray.500'}>
                            {rw.avg_efficiency != null ? `${rw.avg_efficiency}%` : '—'}
                        </Text>
                    </Box>
                    <Box bg={diagBg} p={3} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                        <Text fontSize="2xs" color={mutedColor} fontWeight="600" textTransform="uppercase">Avg Scan Time</Text>
                        <Text fontSize="xl" fontWeight="800" fontFamily="mono">
                            {formatDuration(rw.avg_duration_ms)}
                        </Text>
                    </Box>
                    <Box bg={diagBg} p={3} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                        <Text fontSize="2xs" color={mutedColor} fontWeight="600" textTransform="uppercase">Consecutive Errors</Text>
                        <Text fontSize="xl" fontWeight="800" fontFamily="mono"
                            color={healthReport.consecutive_errors > 0 ? 'red.500' : 'green.500'}>
                            {healthReport.consecutive_errors ?? 0}
                        </Text>
                    </Box>
                </SimpleGrid>

                {/* Last scan corrections breakdown */}
                {healthReport.last_scan?.corrections && (
                    <Box mt={3} bg={diagBg} p={3} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                        <Text fontSize="2xs" color={mutedColor} fontWeight="600" textTransform="uppercase" mb={2}>
                            Last Scan Auto-Corrections
                        </Text>
                        <HStack spacing={3} flexWrap="wrap">
                            {Object.entries(healthReport.last_scan.corrections).map(([key, val]) => (
                                <Badge key={key} colorScheme={val > 0 ? 'orange' : 'gray'}
                                    borderRadius="full" px={2} fontSize="2xs" fontFamily="mono">
                                    {key.replace(/_/g, ' ')}: {val}
                                </Badge>
                            ))}
                        </HStack>
                    </Box>
                )}
            </Box>
        </Collapse>
    );
});

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PANEL COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export const WorkOrderWatchdogPanel = memo(({
    workOrders, // Kept for prop signature consistency but unused for metrics
    onSyncComplete,
    onApplyFilter,
    isLoading = false,
}) => {
    // --- ENTERPRISE FIX: Pull Watchdog Health and Fetcher from Global Context ---
    const { watchdogHealth: healthReport, watchdogLoading, refreshWatchdog } = useOperationalHealth();

    // ── Local State ───────────────────────────────────────────────────────────
    const [isSyncing,        setIsSyncing]        = useState(false);
    const [isBackendRunning, setIsBackendRunning] = useState(false);
    const [circuitState,     setCircuitState]     = useState('CLOSED');
    const [showDiagnostics,  setShowDiagnostics]  = useState(false);
    const [liveCorrections,  setLiveCorrections]  = useState(null);

    // Global Metrics State (Replaces client-side array calculation)
    const [globalMetrics, setGlobalMetrics] = useState({
        breached: 0,
        atRisk: 0,
        stagnant: 0,
        aiNeglect: 0,
        unassignedCritical: 0,
        efficiency: null, // --- UPDATE: unknown until the metrics load (was a made-up 100%)
        totalOpen: 0
    });
    const [metricsError, setMetricsError] = useState(null); // --- UPDATE
    const [isMetricsLoading, setIsMetricsLoading] = useState(true);

    // ── Refs ──────────────────────────────────────────────────────────────────
    const debounceTimer = useRef(null);
    const isMounted     = useRef(true);
    const wsSubscribed  = useRef(false);

    const toast = useToast();

    // ── Theme ─────────────────────────────────────────────────────────────────
    const cardBg       = useColorModeValue('white', 'gray.800');
    const borderColor  = useColorModeValue('gray.200', 'gray.700');
    const alertBorder  = useColorModeValue('red.200', 'red.900');
    const dividerColor = useColorModeValue('gray.200', 'gray.700');
    const mutedColor   = useColorModeValue('gray.500', 'gray.400');
    const aiBoxBg      = useColorModeValue('pink.50', 'rgba(213, 63, 140, 0.1)');
    const aiBorderColor = useColorModeValue('pink.200', 'pink.800');
    const aiTextColor  = useColorModeValue('gray.700', 'gray.300');
    const headerGradient = useColorModeValue(
        'linear(to-r, red.600, orange.500)',
        'linear(to-r, red.400, orange.400)'
    );
    const fallbackNoticeBorder = useColorModeValue('orange.200', 'orange.800');
    const fallbackNoticeBg     = useColorModeValue('orange.50', 'rgba(251,211,141,0.08)');
    // --- UPDATE: dark-mode safe
    const titleColor   = useColorModeValue('gray.800', 'gray.100');
    const iconBoxBad   = useColorModeValue('red.50', 'rgba(229,62,62,0.15)');
    const iconBoxGood  = useColorModeValue('green.50', 'rgba(56,161,105,0.15)');
    const aiHeadColor  = useColorModeValue('pink.700', 'pink.200');
    const onSyncRef    = useRef(onSyncComplete);
    onSyncRef.current  = onSyncComplete;
    const toastRef     = useRef(toast);
    toastRef.current   = toast;
    // --- END UPDATE

    // Derive health error if context provides no report after loading completes
    const healthError = !healthReport && !watchdogLoading;

    // ─────────────────────────────────────────────────────────────────────────
    // GLOBAL METRICS FETCHER (Pulls 11k+ aggregates from DB specific to this panel)
    // ─────────────────────────────────────────────────────────────────────────
    const fetchGlobalMetrics = useCallback(async () => {
        if (!isMounted.current) return;
        try {
            const res = await fetch(`${API_BASE}/workorders/watchdog/metrics`, {
                headers: { Authorization: `Bearer ${getAuthToken()}` },
            });
            if (!res.ok) throw new Error('Metrics fetch failed');
            const json = await res.json();
            
            if (isMounted.current && json.data) {
                setGlobalMetrics({
                    breached: json.data.breached || 0,
                    atRisk: json.data.atRisk || 0,
                    stagnant: json.data.stagnant || 0,
                    aiNeglect: json.data.aiNeglect || 0,
                    unassignedCritical: json.data.unassignedCritical || 0,
                    efficiency: json.data.efficiency ?? null, // --- UPDATE
                    totalOpen: json.data.totalOpen || 0,
                });
                setMetricsError(null); // --- UPDATE
            }
        } catch (error) {
            console.error('Watchdog: Failed to fetch global metrics', error);
            if (isMounted.current) setMetricsError(error.message || 'Metrics unavailable'); // --- UPDATE
        } finally {
            if (isMounted.current) setIsMetricsLoading(false);
        }
    }, []);

    // Fetch metrics on mount
    useEffect(() => {
        fetchGlobalMetrics();
    }, [fetchGlobalMetrics]);

    // ─────────────────────────────────────────────────────────────────────────
    // WEBSOCKET SUBSCRIPTIONS
    // Subscribes to 'watchdog_events' and 'work_order_events' channels.
    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (wsSubscribed.current) return;
        wsSubscribed.current = true;

        // ── watchdog_events ───────────────────────────────────────────────────
        const handleWatchdogEvent = (msg) => {
            if (!isMounted.current) return;
            const payload = liveEventBody(msg); // --- UPDATE: the switch never matched the envelope type

            switch (payload?.type) {
                case 'scan_complete':
                    setIsBackendRunning(false);
                    // Note: AIInsightsContext handles updating the healthReport globally.
                    // We only need to handle local UI ephemeral states like the correction banner.
                    if (payload.corrections) {
                        setLiveCorrections(payload.corrections);
                        // Auto-clear live correction banner after 8s
                        setTimeout(() => {
                            if (isMounted.current) setLiveCorrections(null);
                        }, 8_000);
                    }
                    // Refresh local 11k metrics to get the latest totals
                    setTimeout(() => {
                        fetchGlobalMetrics();
                    }, 500);
                    break;

                case 'circuit_open':
                    setCircuitState('OPEN');
                    toastRef.current({
                        title: '⚠️ Watchdog Circuit OPEN',
                        description: 'The Work Order Watchdog has tripped its circuit breaker due to repeated backend errors.',
                        status: 'error',
                        duration: 8_000,
                        isClosable: true,
                        position: 'top-right',
                    });
                    break;

                // --- UPDATE
                case 'circuit_half_open':
                    setCircuitState('HALF_OPEN');
                    break;
                // --- END UPDATE
                case 'circuit_closed':
                    setCircuitState('CLOSED');
                    toastRef.current({
                        title: '✅ Watchdog Circuit Restored',
                        description: 'The Watchdog is back to normal operation.',
                        status: 'success',
                        duration: 4_000,
                        isClosable: true,
                        position: 'top-right',
                    });
                    break;

                default:
                    break;
            }
        };

        // ── work_order_events (debounced — coalesce rapid bursts) ─────────────
        const handleWorkOrderEvent = (msg) => {
            if (!isMounted.current) return;
            const payload = liveEventBody(msg); // --- UPDATE

            // Only act on watchdog-originated events; other WO events handled by parent
            const watchdogTypes = new Set([
                'sla_watchdog_update',
                'stagnant_flagged',
                'ai_neglect_retriggered',
                'unassigned_critical',
            ]);
            if (!watchdogTypes.has(payload?.type)) return;

            // Debounce: cancel pending timer and restart
            clearTimeout(debounceTimer.current);
            debounceTimer.current = setTimeout(() => {
                if (!isMounted.current) return;
                // Fetch updated 11k+ metrics and trigger parent to re-fetch WO list
                fetchGlobalMetrics();
                onSyncRef.current?.(); // --- UPDATE: ref, not a dependency
            }, DEBOUNCE_RENDER_MS);
        };

        // --- UPDATE: plain work-order lifecycle events change the totals too (metrics only)
        const handleLifecycle = () => {
            if (!isMounted.current) return;
            clearTimeout(debounceTimer.current);
            debounceTimer.current = setTimeout(() => { if (isMounted.current) fetchGlobalMetrics(); }, DEBOUNCE_RENDER_MS);
        };
        // --- END UPDATE

        webSocketService.subscribe('watchdog_events', handleWatchdogEvent);
        webSocketService.subscribe('work_order_events', handleWorkOrderEvent);
        LIVE_WO_EVENTS.forEach(evt => webSocketService.subscribe(evt, handleLifecycle)); // --- UPDATE

        return () => {
            webSocketService.unsubscribe('watchdog_events', handleWatchdogEvent);
            webSocketService.unsubscribe('work_order_events', handleWorkOrderEvent);
            LIVE_WO_EVENTS.forEach(evt => webSocketService.unsubscribe(evt, handleLifecycle)); // --- UPDATE
            clearTimeout(debounceTimer.current);
            wsSubscribed.current = false;
        };
    }, [fetchGlobalMetrics]); // --- UPDATE: was [fetchGlobalMetrics, onSyncComplete, toast]

    // --- UPDATE: visible-tab polling so the tiles never go stale between scans
    useEffect(() => {
        const id = setInterval(() => {
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
            fetchGlobalMetrics();
        }, METRICS_POLL_MS);
        return () => clearInterval(id);
    }, [fetchGlobalMetrics]);
    // --- END UPDATE

    // Cleanup on unmount
    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; clearTimeout(debounceTimer.current); };
    }, []);

    // ─────────────────────────────────────────────────────────────────────────
    // TRIGGER BACKEND SCAN
    // ─────────────────────────────────────────────────────────────────────────
    const handleTriggerScan = useCallback(async () => {
        if (isSyncing || isBackendRunning) return;
        setIsSyncing(true);
        setIsBackendRunning(true);
        try {
            const res = await fetch(`${API_BASE}/workorders/watchdog/sync`, {
    method: 'POST',
    headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAuthToken()}`,
    },
});

            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json = await res.json();
            const report = json.data || json;

            onSyncComplete?.();
            setTimeout(() => {
                refreshWatchdog(); // Use the global context's fetcher
                fetchGlobalMetrics();
            }, 500);

            const c = report.corrections_applied || report.corrections || {};
            toast({
                title: '🔍 Watchdog Scan Complete',
                description: `Auto-corrected ${c.newly_at_risk ?? 0} at-risk and ${c.newly_breached ?? 0} breached tickets.`,
                status: 'success',
                duration: 6_000,
                isClosable: true,
            });
        } catch (err) {
            toast({
                title: 'Scan Failed',
                description: err.message || 'Could not reach the Watchdog service.',
                status: 'error',
                duration: 4_000,
                isClosable: true,
            });
        } finally {
            setIsSyncing(false);
            // isBackendRunning will be cleared by the WS 'scan_complete' event
            // Fallback: clear after 30s if WS event doesn't arrive
            setTimeout(() => { if (isMounted.current) setIsBackendRunning(false); }, 30_000);
        }
    }, [isSyncing, isBackendRunning, onSyncComplete, refreshWatchdog, fetchGlobalMetrics, toast]);

    // ─────────────────────────────────────────────────────────────────────────
    // DERIVED DISPLAY STATE
    // ─────────────────────────────────────────────────────────────────────────
    const effKnown           = globalMetrics.efficiency !== null && globalMetrics.efficiency !== undefined; // --- UPDATE
    const effColor           = effKnown ? efficiencyColor(globalMetrics.efficiency) : 'gray';
    const isUnhealthy        = effKnown && globalMetrics.efficiency < EFFICIENCY_THRESHOLDS.WARNING;
    const hasActionableItems = globalMetrics.breached > 0 || globalMetrics.aiNeglect > 0 || globalMetrics.unassignedCritical > 0;
    const lastScan           = healthReport?.last_scan ?? null;
    const isFirstLoad        = isLoading || isMetricsLoading;

    // Hide panel entirely when healthy and empty (nothing to show)
    if (!isFirstLoad && !metricsError && globalMetrics.totalOpen === 0 && circuitState === 'CLOSED') return null; // --- UPDATE: keep visible when metrics failed

    return (
        <MotionBox
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
        >
            <Box
                bg={cardBg}
                borderRadius="2xl"
                borderWidth="1px"
                borderColor={isUnhealthy || circuitState === 'OPEN' ? alertBorder : borderColor}
                overflow="hidden"
                boxShadow={isUnhealthy ? '0 0 0 3px var(--chakra-colors-red-100)' : 'sm'}
                position="relative"
                transition="all 0.3s"
            >
                {/* ── Accent bar ─────────────────────────────────────────────── */}
                <Box
                    position="absolute" top={0} left={0} right={0} h="3px"
                    bgGradient={isUnhealthy
                        ? 'linear(to-r, red.400, orange.400)'
                        : 'linear(to-r, green.400, teal.400)'
                    }
                    borderTopRadius="2xl"
                />

                <Box p={5} pt={6}>
                    {/* ── CIRCUIT BREAKER ALERT (shows when OPEN) ──────────────── */}
                    <AnimatePresence>
                        {circuitState !== 'CLOSED' && (
                            <MotionBox
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                mb={4}
                            >
                                <Alert
                                    status={circuitState === 'OPEN' ? 'error' : 'warning'}
                                    borderRadius="xl" fontSize="sm"
                                >
                                    <AlertIcon />
                                    <AlertDescription>
                                        {circuitState === 'OPEN'
                                            ? 'The backend Watchdog has tripped its circuit breaker. Automated SLA scans are paused until the service recovers.'
                                            : 'The Watchdog is probing after a recovery. Monitoring will fully resume shortly.'
                                        }
                                    </AlertDescription>
                                </Alert>
                            </MotionBox>
                        )}
                    </AnimatePresence>

                    {/* --- UPDATE: say when the numbers could not be loaded */}
                    {metricsError && !isFirstLoad && (
                        <Alert status="warning" borderRadius="xl" fontSize="sm" mb={4}>
                            <AlertIcon />
                            <AlertDescription>Watchdog metrics unavailable ({metricsError}). Retrying every 60 s.</AlertDescription>
                        </Alert>
                    )}
                    {/* --- END UPDATE */}
                    {/* ── LIVE CORRECTION BANNER ───────────────────────────────── */}
                    <AnimatePresence>
                        {liveCorrections && (
                            <MotionBox
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                mb={4}
                            >
                                <Alert status="info" borderRadius="xl" fontSize="sm">
                                    <AlertIcon as={MdBolt} />
                                    <AlertDescription>
                                        Live scan applied:{' '}
                                        <strong>{liveCorrections.newly_at_risk ?? 0}</strong> at-risk,{' '}
                                        <strong>{liveCorrections.newly_breached ?? 0}</strong> breached,{' '}
                                        <strong>{liveCorrections.stagnant_flagged ?? 0}</strong> stagnant flagged,{' '}
                                        <strong>{liveCorrections.ai_re_dispatched ?? 0}</strong> AI re-dispatched.
                                    </AlertDescription>
                                </Alert>
                            </MotionBox>
                        )}
                    </AnimatePresence>

                    {/* ── HEADER ROW ───────────────────────────────────────────── */}
                    <Flex justify="space-between" align="flex-start" wrap="wrap" gap={4} mb={5}>
                        <HStack spacing={4} align="flex-start">
                            <MotionBox
                                whileHover={{ rotate: 10, scale: 1.1 }}
                                transition={{ type: 'spring', stiffness: 400 }}
                            >
                                <Box
                                    bg={isUnhealthy ? iconBoxBad : iconBoxGood} // --- UPDATE: dark-mode safe
                                    p={3} borderRadius="xl"
                                    boxShadow={isUnhealthy
                                        ? '0 4px 14px var(--chakra-colors-red-200)'
                                        : '0 4px 14px var(--chakra-colors-green-200)'
                                    }
                                >
                                    <Icon
                                        as={MdSecurity}
                                        color={isUnhealthy ? 'red.500' : 'green.500'}
                                        boxSize={6}
                                    />
                                </Box>
                            </MotionBox>
                            <Box>
                                <HStack spacing={2} align="center">
                                    <Text
                                        fontSize="md" fontWeight="800"
                                        bgGradient={isUnhealthy ? headerGradient : undefined}
                                        bgClip={isUnhealthy ? 'text' : undefined}
                                        color={isUnhealthy ? undefined : titleColor} // --- UPDATE
                                    >
                                        Dispatch & SLA Watchdog
                                    </Text>
                                    <CircuitBadge state={circuitState} />
                                    {(isBackendRunning || isSyncing) && (
                                        <MotionBox
                                            animate={{ rotate: 360 }}
                                            transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
                                        >
                                            <Icon as={MdAutorenew} color="blue.500" boxSize={4} />
                                        </MotionBox>
                                    )}
                                </HStack>
                                <Text fontSize="xs" color={mutedColor} mt={0.5}>
                                    Autonomous monitoring of SLA compliance, stagnant tickets, and AI dispatch health.
                                    {globalMetrics.totalOpen > 0 && (
                                        <Text as="span" ml={2} fontWeight="700" color={mutedColor}>
                                            {globalMetrics.totalOpen.toLocaleString()} active tickets tracked.
                                        </Text>
                                    )}
                                </Text>
                            </Box>
                        </HStack>

                        {/* ── EFFICIENCY GAUGE ───────────────────────────────── */}
                        <VStack align="flex-end" spacing={2} minW="220px">
                            <HStack justify="space-between" w="full">
                                <HStack spacing={1}>
                                    <Icon as={MdTrendingUp} color={`${effColor}.500`} boxSize={4} />
                                    <Text fontSize="xs" fontWeight="700" color={mutedColor}>
                                        Network Efficiency
                                    </Text>
                                    <WoHint metric="efficiency" />{/* --- UPDATE */}
                                </HStack>
                                <AnimatePresence mode="wait">
                                    <MotionBox
                                        key={globalMetrics.efficiency}
                                        initial={{ scale: 0.8, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        transition={{ type: 'spring', stiffness: 300 }}
                                    >
                                        <Text as="div" fontSize="xl" fontWeight="900" color={`${effColor}.500`}>{/* --- UPDATE: Skeleton (div) inside <p> was invalid DOM */}
                                            {isFirstLoad ? <Skeleton w="48px" h="24px" /> : (effKnown ? `${globalMetrics.efficiency}%` : '—')}{/* --- UPDATE */}
                                        </Text>
                                    </MotionBox>
                                </AnimatePresence>
                            </HStack>
                            <Progress
                                value={isFirstLoad || !effKnown ? 0 : globalMetrics.efficiency}
                                w="full" size="md" borderRadius="full"
                                colorScheme={effColor}
                                hasStripe
                                isAnimated={(effKnown && globalMetrics.efficiency < 100) || isBackendRunning}
                            />

                            {/* Rolling avg from backend */}
                            {healthReport?.rolling_window?.avg_efficiency != null && (
                                <Text fontSize="2xs" color={mutedColor} w="full" textAlign="right">
                                    10-scan avg: {healthReport.rolling_window.avg_efficiency}%
                                    {healthReport.rolling_window.avg_duration_ms != null && (
                                        <Text as="span" ml={2}>
                                            · {formatDuration(healthReport.rolling_window.avg_duration_ms)}/scan
                                        </Text>
                                    )}
                                </Text>
                            )}

                            <HStack spacing={2} mt={1}>
                                <Button
                                    size="xs" colorScheme="blue" variant="outline"
                                    leftIcon={<Icon as={MdAutorenew} />}
                                    onClick={handleTriggerScan}
                                    isLoading={isSyncing}
                                    isDisabled={isBackendRunning || circuitState === 'OPEN'}
                                    borderRadius="lg"
                                >
                                    Run Scan
                                </Button>
                                <Tooltip
                                    label={showDiagnostics ? 'Hide backend diagnostics' : 'Show backend diagnostics'}
                                    hasArrow
                                >
                                    <Button
                                        size="xs" variant="ghost" colorScheme="gray"
                                        leftIcon={<Icon as={MdAnalytics} />}
                                        rightIcon={<Icon as={showDiagnostics ? MdExpandLess : MdExpandMore} />}
                                        onClick={() => setShowDiagnostics(v => !v)}
                                        borderRadius="lg"
                                    >
                                        Diagnostics
                                    </Button>
                                </Tooltip>
                            </HStack>
                        </VStack>
                    </Flex>

                    {/* ── LAST SCAN INFO BAR ───────────────────────────────────── */}
                    {(lastScan || isBackendRunning || isSyncing) && (
                        <Box mb={5}>
                            <LastScanBar
                                lastScan={lastScan}
                                isScanning={isBackendRunning || isSyncing}
                            />
                        </Box>
                    )}

                    <Divider borderColor={dividerColor} mb={5} />

                    {/* ── METRICS GRID ─────────────────────────────────────────── */}
                    <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={4}>
                        <WatchdogStat
                            label="SLA Breached"
                            count={globalMetrics.breached}
                            icon={MdErrorOutline}
                            color="red"
                            isLoading={isFirstLoad}
                            tooltip="Tickets past their SLA resolution deadline. Click to filter."
                            onClick={() => onApplyFilter?.('sla_status', 'breached')}
                            hint="breached"
                        />
                        <WatchdogStat
                            label="SLA At Risk"
                            count={globalMetrics.atRisk}
                            icon={FiAlertTriangle}
                            color="orange"
                            isLoading={isFirstLoad}
                            tooltip="Tickets within 4h of their SLA deadline. Click to filter."
                            onClick={() => onApplyFilter?.('sla_status', 'at_risk')}
                            hint="at_risk"
                        />
                        <WatchdogStat
                            label="Stagnant (24h+)"
                            count={globalMetrics.stagnant}
                            icon={MdTimer}
                            color="yellow"
                            isLoading={isFirstLoad}
                            tooltip="In-progress tickets with no update in 24+ hours. Click to filter."
                            onClick={() => onApplyFilter?.('stagnant', true)} // --- UPDATE: was status=in_progress
                            hint="stagnant"
                        />
                        <WatchdogStat
                            label="AI Ignored"
                            count={globalMetrics.aiNeglect}
                            icon={MdOutlineAutoAwesome}
                            color="purple"
                            isLoading={isFirstLoad}
                            tooltip="AI suggested a technician but the ticket remains unassigned. Click to filter."
                            onClick={() => onApplyFilter?.('suggestion', 'has_suggestion')}
                            hint="ai_ignored"
                        />
                        <WatchdogStat
                            label="Unassigned Critical"
                            count={globalMetrics.unassignedCritical}
                            icon={MdPersonOff}
                            color="red"
                            isLoading={isFirstLoad}
                            tooltip="High or Critical priority tickets with no assigned owner. Click to filter."
                            onClick={() => onApplyFilter?.('unassigned_critical', true)}
                            hint="unassigned_critical"
                        />
                    </SimpleGrid>

                    {/* ── AI PRESCRIPTIVE ACTIONS ──────────────────────────────── */}
                    <AnimatePresence>
                        {hasActionableItems && !isFirstLoad && (
                            <MotionBox
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.3 }}
                                mt={5}
                                bg={aiBoxBg}
                                p={4}
                                borderRadius="xl"
                                borderWidth="1px"
                                borderColor={aiBorderColor}
                            >
                                <HStack mb={2} justify="space-between">
                                    <HStack>
                                        <Icon as={MdOutlineAutoAwesome} color="pink.500" boxSize={5} />
                                        <Text fontSize="sm" fontWeight="800" color={aiHeadColor}>
                                            AI Prescriptive Actions
                                        </Text>
                                    </HStack>
                                    <Badge colorScheme="pink" borderRadius="full" fontSize="2xs">
                                        {[globalMetrics.breached, globalMetrics.unassignedCritical, globalMetrics.aiNeglect]
                                            .filter(v => v > 0).length} issue{[globalMetrics.breached, globalMetrics.unassignedCritical, globalMetrics.aiNeglect].filter(v => v > 0).length !== 1 ? 's' : ''} requiring action
                                    </Badge>
                                </HStack>
                                <VStack align="start" spacing={2} pl={7}>
                                    {globalMetrics.breached > 0 && (
                                        <Flex gap={2} align="flex-start">
                                            <Badge colorScheme="red" fontSize="2xs" mt={0.5} flexShrink={0}>URGENT</Badge>
                                            <Text fontSize="xs" color={aiTextColor} lineHeight="1.5">
                                                <Text as="span" fontWeight="700">SLA Breached × {globalMetrics.breached}:</Text>{' '}
                                                Escalate to management or re-route to available technicians immediately.{' '}
                                                <Text
                                                    as="span"
                                                    color="red.500"
                                                    fontWeight="700"
                                                    cursor="pointer"
                                                    _hover={{ textDecoration: 'underline' }}
                                                    onClick={() => onApplyFilter?.('sla_status', 'breached')}
                                                >
                                                    View breached →
                                                </Text>
                                            </Text>
                                        </Flex>
                                    )}
                                    {globalMetrics.unassignedCritical > 0 && (
                                        <Flex gap={2} align="flex-start">
                                            <Badge colorScheme="orange" fontSize="2xs" mt={0.5} flexShrink={0}>CRITICAL</Badge>
                                            <Text fontSize="xs" color={aiTextColor} lineHeight="1.5">
                                                <Text as="span" fontWeight="700">Unassigned Critical × {globalMetrics.unassignedCritical}:</Text>{' '}
                                                High-priority tickets without an owner risk infrastructure failure.{' '}
                                                <Text
                                                    as="span"
                                                    color="orange.500"
                                                    fontWeight="700"
                                                    cursor="pointer"
                                                    _hover={{ textDecoration: 'underline' }}
                                                    onClick={() => onApplyFilter?.('unassigned_critical', true)}
                                                >
                                                    Assign now →
                                                </Text>
                                            </Text>
                                        </Flex>
                                    )}
                                    {globalMetrics.aiNeglect > 0 && (
                                        <Flex gap={2} align="flex-start">
                                            <Badge colorScheme="purple" fontSize="2xs" mt={0.5} flexShrink={0}>EFFICIENCY</Badge>
                                            <Text fontSize="xs" color={aiTextColor} lineHeight="1.5">
                                                <Text as="span" fontWeight="700">AI Suggestions Waiting × {globalMetrics.aiNeglect}:</Text>{' '}
                                                Optimal technicians have been identified. Batch-approve them to cut response time.{' '}
                                                <Text
                                                    as="span"
                                                    color="purple.500"
                                                    fontWeight="700"
                                                    cursor="pointer"
                                                    _hover={{ textDecoration: 'underline' }}
                                                    onClick={() => onApplyFilter?.('suggestion', 'has_suggestion')}
                                                >
                                                    Review suggestions →
                                                </Text>
                                            </Text>
                                        </Flex>
                                    )}
                                </VStack>
                            </MotionBox>
                        )}
                    </AnimatePresence>

                    {/* ── BACKEND DIAGNOSTICS (Collapsible) ───────────────────── */}
                    {!healthError && (
                        <BackendDiagnostics
                            healthReport={healthReport}
                            isOpen={showDiagnostics}
                        />
                    )}

                    {/* Health endpoint unavailable notice */}
                    {healthError && showDiagnostics && (
                        <Box mt={4} p={3} borderRadius="lg" borderWidth="1px"
                            borderColor={fallbackNoticeBorder}
                            bg={fallbackNoticeBg}>
                            <HStack>
                                <Icon as={MdNetworkCheck} color="orange.400" boxSize={4} />
                                <Text fontSize="xs" color="orange.600">
                                    Backend health endpoint unreachable. Panel is running in client-only mode with live WebSocket data.
                                </Text>
                            </HStack>
                        </Box>
                    )}
                </Box>
            </Box>
        </MotionBox>
    );
});

WorkOrderWatchdogPanel.displayName = 'WorkOrderWatchdogPanel';

export default WorkOrderWatchdogPanel;